import React, { useRef, useState, useEffect } from 'react';
import Tooltip from '../tooltip/Tooltip';

interface TruncatedTextWithTooltipProps {
  text: string | null | undefined;
  children: React.ReactNode;
  position?: 'top' | 'right' | 'bottom' | 'left' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Componente que envuelve contenido de texto y muestra un tooltip solo cuando el texto está truncado.
 * Detecta automáticamente si el contenido está truncado comparando scrollWidth con clientWidth.
 */
const TruncatedTextWithTooltip: React.FC<TruncatedTextWithTooltipProps> = ({
  text,
  children,
  position = 'top',
  className,
  style,
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const element = textRef.current;
        // Verificar si el texto está truncado
        // Usar un pequeño margen (1px) para evitar falsos positivos por redondeo
        const truncated = element.scrollWidth > element.clientWidth + 1;
        setIsTruncated(truncated);
      }
    };

    // Usar requestAnimationFrame para asegurar que el DOM esté completamente renderizado
    const timeoutId = setTimeout(() => {
      checkTruncation();
    }, 0);

    // Verificar truncamiento cuando cambia el tamaño de la ventana
    window.addEventListener('resize', checkTruncation);

    // Usar MutationObserver para detectar cambios en el contenido
    const observer = new MutationObserver(() => {
      // Usar un pequeño delay para permitir que el DOM se actualice
      setTimeout(checkTruncation, 0);
    });
    if (textRef.current) {
      observer.observe(textRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTruncation);
      observer.disconnect();
    };
  }, [text]);

  // Si no hay texto o el texto está vacío, no mostrar tooltip
  const displayText = text || '';
  const shouldShowTooltip = isTruncated && displayText.trim() !== '';

  const content = (
    <div
      ref={textRef}
      className={className}
      style={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
        maxWidth: '100%',
        width: '100%',
        ...style,
      }}
    >
      {children}
    </div>
  );

  if (shouldShowTooltip) {
    return (
      <Tooltip position={position} msg={displayText}>
        {content}
      </Tooltip>
    );
  }

  return content;
};

export default TruncatedTextWithTooltip;
